import { useMemo, useState } from 'react'
import { api, errorText } from '../api.ts'
import { ALERT_KINDS, jobLabel, SOURCE_TYPES, useJobs, useJobsAction } from '../jobs.ts'
import type { Job, JobFlag, JobImport, JobsDetail, JobSource } from '../types.ts'
import { Badge, Field, Loading, Modal, Notice, when } from '../ui.tsx'
import { Button } from '../components/Button.tsx'
import { SearchBox } from '../components/SearchBox.tsx'

/**
 * A company's jobs (PLAN.md 1.6, 1.7): the source's status and alerts, the review list, and
 * the job list. Company administrators archive jobs and link renumbered ones. The jobs
 * themselves come from the company's job source, never typed in here.
 */
export function Jobs({ companyId }: { companyId: string }) {
  const jobs = useJobs(companyId)
  const act = useJobsAction(companyId)
  const [linking, setLinking] = useState<Job | null>(null)

  if (jobs.isPending) return <Loading />
  if (jobs.isError) return <Notice>{errorText(jobs.error)}</Notice>
  const data = jobs.data
  const byId = new Map(data.jobs.map((j) => [j.id, j]))
  const base = `/companies/${companyId}`

  const run = (path: string, method: 'put' | 'post' | 'delete', body?: unknown) =>
    act.mutate(() =>
      method === 'delete' ? api.delete<JobsDetail>(path) : method === 'put' ? api.put<JobsDetail>(path, body) : api.post<JobsDetail>(path, body),
    )
  const archive = (job: Job, archived: boolean) => run(`${base}/jobs/${job.id}/archived`, 'put', { archived, version: job.version })
  const dismiss = (flag: JobFlag) => run(`${base}/job-flags/${flag.id}/dismiss`, 'post')

  const alerts = data.flags.filter((f) => ALERT_KINDS.includes(f.kind))
  const review = data.flags.filter((f) => !ALERT_KINDS.includes(f.kind))

  return (
    <section>
      <h1>Jobs</h1>
      <Notice>{act.isError && errorText(act.error)}</Notice>
      {alerts.map((flag) => (
        <div key={flag.id} className="notice notice-error alert-row" role="alert">
          <span>{alertText(flag, data.sources)}</span>
          <Button variant="tertiary" size="small" disabled={act.isPending} onClick={() => dismiss(flag)}>Dismiss</Button>
        </div>
      ))}

      <Sources sources={data.sources} imports={data.imports} />

      <div className="card">
        <h2>To review {review.length > 0 && <Badge tone="warn">{review.length}</Badge>}</h2>
        {review.length === 0 ? (
          <p className="muted">Nothing to review.</p>
        ) : (
          <ul className="review">
            {review.map((flag) => (
              <li key={flag.id}>
                <ReviewItem flag={flag} byId={byId} busy={act.isPending} onArchive={(j) => archive(j, true)} onLink={setLinking} onDismiss={() => dismiss(flag)} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <JobList jobs={data.jobs} byId={byId} busy={act.isPending} onArchive={archive} onLink={setLinking} onUnlink={(j) => run(`${base}/jobs/${j.id}/alias`, 'delete')} />

      <Modal title="Link to the job that replaced it" open={linking !== null} onClose={() => setLinking(null)}>
        {linking && (
          <LinkForm
            job={linking}
            jobs={data.jobs}
            onLink={async (newJobId) => {
              act.mutate(() => api.put<JobsDetail>(`${base}/jobs/${linking.id}/alias`, { newJobId }), { onSuccess: () => setLinking(null) })
            }}
          />
        )}
      </Modal>
    </section>
  )
}

function alertText(flag: JobFlag, sources: JobSource[]): string {
  const source = sources.find((s) => s.id === flag.sourceId)?.name ?? 'The job source'
  switch (flag.kind) {
    case 'stale_source': {
      const last = flag.detail.lastGoodImportAt as string | null
      return `${source} hasn't sent a good report since ${last ? when(last) : 'it was set up'}. The last good job list is still in use.`
    }
    case 'malformed_report':
      return `A report from ${source} couldn't be used, so nothing in it was applied: ${String(flag.detail.reason ?? 'unreadable')}.`
    case 'rejected_rows': {
      const rows = (flag.detail.rows as Array<{ line: number; key: string | null; reason: string }>) ?? []
      const shown = rows.slice(0, 5).map((r) => `line ${r.line}${r.key ? ` (#${r.key})` : ''}: ${r.reason}`).join('; ')
      return `${String(flag.detail.total ?? rows.length)} rows of a report from ${source} were set aside, and the rest applied. ${shown}${rows.length > 5 ? '…' : ''}`
    }
    default:
      return flag.kind
  }
}

function ReviewItem({
  flag,
  byId,
  busy,
  onArchive,
  onLink,
  onDismiss,
}: {
  flag: JobFlag
  byId: Map<string, Job>
  busy: boolean
  onArchive: (job: Job) => void
  onLink: (job: Job) => void
  onDismiss: () => void
}) {
  const job = flag.jobId ? byId.get(flag.jobId) : undefined
  const other = flag.otherJobId ? byId.get(flag.otherJobId) : undefined
  let text: string
  let actions: React.ReactNode = null
  switch (flag.kind) {
    case 'possible_duplicate':
      text = `${jobLabel(job)} and ${jobLabel(other)} may be the same job (${((flag.detail.why as string[]) ?? []).join(', ')}).`
      actions = (
        <>
          {job && <Button variant="tertiary" size="small" disabled={busy} onClick={() => onArchive(job)}>Archive #{job.number}</Button>}
          {other && <Button variant="tertiary" size="small" disabled={busy} onClick={() => onArchive(other)}>Archive #{other.number}</Button>}
          <Button variant="tertiary" size="small" disabled={busy} onClick={onDismiss}>Not duplicates</Button>
        </>
      )
      break
    case 'unknown_status':
      text = `${jobLabel(job)} has the status "${String(flag.detail.value ?? '')}", which isn't mapped to Open or Closed. It's counted as Open. Ask Icefield to map it.`
      actions = <Button variant="tertiary" size="small" disabled={busy} onClick={onDismiss}>Dismiss</Button>
      break
    case 'not_in_source':
      text = `${jobLabel(job)} hasn't been in the last ${String(flag.detail.reports ?? 3)} reports. It might be finished, or renumbered.`
      actions = (
        <>
          {job && <Button variant="tertiary" size="small" disabled={busy} onClick={() => onArchive(job)}>Archive</Button>}
          {job && <Button variant="tertiary" size="small" disabled={busy} onClick={() => onLink(job)}>Link to new number…</Button>}
          <Button variant="tertiary" size="small" disabled={busy} onClick={onDismiss}>Keep</Button>
        </>
      )
      break
    default:
      text = flag.kind
  }
  return (
    <div className="review-item">
      <span>{text}</span>
      <span className="row">{actions}</span>
    </div>
  )
}

const IMPORT_STATES: Record<JobImport['state'], { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }> = {
  queued: { label: 'Queued', tone: 'neutral' },
  applied: { label: 'Applied', tone: 'ok' },
  duplicate: { label: 'Same as last', tone: 'neutral' },
  out_of_order: { label: 'Older, ignored', tone: 'warn' },
  malformed: { label: 'Not usable', tone: 'bad' },
  failed: { label: 'Failed, retrying', tone: 'bad' },
}

function Sources({ sources, imports }: { sources: JobSource[]; imports: JobImport[] }) {
  if (!sources.length) {
    return (
      <div className="card">
        <h2>Job source</h2>
        <p className="muted">No job source is set up yet. Icefield sets it up from your DASH report or Smartsheet.</p>
      </div>
    )
  }
  return (
    <div className="card">
      <h2>Job source</h2>
      {sources.map((s) => (
        <p key={s.id}>
          <strong>{s.name}</strong> <span className="muted small">{SOURCE_TYPES[s.type]}</span>{' '}
          {!s.enabled && <Badge tone="warn">Off</Badge>}
          <br />
          <span className="small">Last good report: {s.lastGoodImportAt ? when(s.lastGoodImportAt) : 'none yet'}</span>
        </p>
      ))}
      {imports.length > 0 && (
        <details>
          <summary className="small">Recent reports</summary>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Received</th><th>File</th><th>Result</th><th className="num">New</th><th className="num">Changed</th><th className="num">Set aside</th></tr>
              </thead>
              <tbody>
                {imports.map((i) => (
                  <tr key={i.id}>
                    <td className="nowrap">{when(i.receivedAt)}</td>
                    <td className="small">{i.fileName ?? (i.receivedVia === 'upload' ? 'Upload' : 'Connector')}</td>
                    <td><span title={i.error ?? ''}><Badge tone={IMPORT_STATES[i.state].tone}>{IMPORT_STATES[i.state].label}</Badge></span></td>
                    <td className="num">{i.created ?? '—'}</td>
                    <td className="num">{i.updated ?? '—'}</td>
                    <td className="num">{i.rejected ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  )
}

type Filter = 'open' | 'closed' | 'archived' | 'all'

function JobList({
  jobs,
  byId,
  busy,
  onArchive,
  onLink,
  onUnlink,
}: {
  jobs: Job[]
  byId: Map<string, Job>
  busy: boolean
  onArchive: (job: Job, archived: boolean) => void
  onLink: (job: Job) => void
  onUnlink: (job: Job) => void
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('open')
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return jobs.filter((j) => {
      const inFilter =
        filter === 'all' || (filter === 'archived' ? j.archived : !j.archived && j.status === filter)
      return inFilter && (!q || j.number.toLowerCase().includes(q) || j.name.toLowerCase().includes(q))
    })
  }, [jobs, query, filter])

  return (
    <div className="card">
      <div className="section-head">
        <h2>All jobs</h2>
        <div className="row">
          <SearchBox placeholder="Number or name" label="Search jobs" value={query} onChange={setQuery} width={240} />
          <select value={filter} onChange={(e) => setFilter(e.target.value as Filter)} aria-label="Show">
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Number</th><th>Name</th><th>Status</th><th>PM</th><th aria-label="Actions" /></tr>
          </thead>
          <tbody>
            {shown.map((j) => (
              <tr key={j.id} className={j.archived ? 'dim' : undefined}>
                <td className="nowrap"><strong>#{j.number}</strong></td>
                <td>
                  {j.name}
                  {j.aliasOf && <div className="small muted">Replaced by {jobLabel(byId.get(j.aliasOf))}</div>}
                </td>
                <td>
                  <Badge tone={j.status === 'open' ? 'ok' : 'neutral'}>{j.status}</Badge> {j.archived && <Badge>archived</Badge>}
                </td>
                <td className="small">{j.pmName ?? '—'}</td>
                <td className="actions">
                  {j.aliasOf ? (
                    <Button variant="tertiary" size="small" disabled={busy} onClick={() => onUnlink(j)}>Unlink</Button>
                  ) : (
                    <Button variant="tertiary" size="small" disabled={busy} onClick={() => onLink(j)}>Link…</Button>
                  )}
                  <Button variant="tertiary" size="small" disabled={busy} onClick={() => onArchive(j, !j.archived)}>
                    {j.archived ? 'Restore' : 'Archive'}
                  </Button>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={5} className="muted">No jobs here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function LinkForm({ job, jobs, onLink }: { job: Job; jobs: Job[]; onLink: (newJobId: string) => Promise<void> }) {
  const candidates = jobs.filter((j) => j.id !== job.id && !j.aliasOf && !j.archived)
  const [target, setTarget] = useState(candidates[0]?.id ?? '')
  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault()
        if (target) void onLink(target)
      }}
    >
      <p>
        <strong>#{job.number} {job.name}</strong> was renumbered. Apps will group it with the job that replaced it.
      </p>
      <Field label="Replaced by">
        <select value={target} onChange={(e) => setTarget(e.target.value)} required>
          {candidates.map((j) => (
            <option key={j.id} value={j.id}>#{j.number} {j.name}</option>
          ))}
        </select>
      </Field>
      <div className="form-actions">
        <Button type="submit" disabled={!target}>Link</Button>
      </div>
    </form>
  )
}
